import React, { useState, useEffect } from 'react';
import './index.css';
import './App.css'; 

import Login from './Login';
import Dashboard from './Dashboard';
import Ventas from './Ventas';
import HistorialVentas from './Historial';
import Inventario from './Inventario';
import Promociones from './Promociones';
import Caja from './Caja';
import Reportes from './Reportes';
import Clientes from './Clientes';
import Proveedores from './Proveedores';
import Compras from './Compras';
import Configuracion from './Configuracion';
import Usuarios from './Usuarios';
import Kardex from './Kardex';
import InventariosFisicos from './InventariosFisicos';
import Precios from './Precios';
import Facturacion from './Facturacion';
import Reposicion from './Reposicion';
import Organizacion from './Organizacion';
import Transferencias from './Transferencias';
import Lotes from './Lotes';
import OfflineStatus from './OfflineStatus';
import MercadoPago from './MercadoPago';
import { ErrorBoundary } from './ErrorBoundary';
import { api, getUsuarioGuardado, setOnSesionExpirada, limpiarSesion, setSesion } from './api';
import { BRANDING_POR_DEFECTO, cargarBrandingPublico, aplicarBrandingDocumento } from './branding';

export default function App() {
  // ANTES: la sesión vivía solo en memoria (useState(null)), así que
  // recargar la página (F5) te mandaba al login de nuevo aunque el token
  // siguiera siendo válido. Ahora se restaura desde localStorage al cargar.
  const [usuario, setUsuario] = useState(() => getUsuarioGuardado());
  const [vista, setVista] = useState('dashboard');
  const [filtroInventario, setFiltroInventario] = useState('todos');
  const [menuOpen, setMenuOpen] = useState(false); // Para móviles
  const [branding, setBranding] = useState(BRANDING_POR_DEFECTO);
  const [contextos,setContextos]=useState([]);
  const [cambiandoContexto,setCambiandoContexto]=useState(false);
  const [online,setOnline]=useState(() => typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    const conectado = () => setOnline(true);
    const desconectado = () => setOnline(false);
    window.addEventListener('online', conectado);
    window.addEventListener('offline', desconectado);
    return () => {
      window.removeEventListener('online', conectado);
      window.removeEventListener('offline', desconectado);
    };
  }, []);

  useEffect(() => {
    let activo=true;
    cargarBrandingPublico(usuario?.empresa_id || 1).then((data)=>{
      if(!activo)return;
      setBranding(data);
      aplicarBrandingDocumento(data);
    });
    return()=>{activo=false;};
  },[usuario?.empresa_id]);

  const actualizarBranding = React.useCallback((data) => {
    const siguiente = { ...BRANDING_POR_DEFECTO, ...(data || {}) };
    setBranding(siguiente);
    aplicarBrandingDocumento(siguiente);
  }, []);

  // P0.14: hook de resize. Antes se leía window.innerWidth directo en el render,
  // que no se re-evalúa al rotar/redimensionar. Ahora escuchamos 'resize' y
  // guardamos el ancho en estado, así el layout responde de verdad.
  const [esMovil, setEsMovil] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  useEffect(() => {
    const onResize = () => setEsMovil(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // P0.14: estado real de la caja (abierta/cerrada + cajero + hora de apertura),
  // tomado de /api/caja/actual en vez del texto fijo "Caja abierta".
  const [estadoCaja, setEstadoCaja] = useState({ abierta: false, cajero: null, desde: null, propia: false });
  const cargarEstadoCaja = React.useCallback(async () => {
    try {
      const data = await api.get('/api/caja/actual');
      if (data && data.abierta && data.turno) {
        setEstadoCaja({
          abierta: true,
          cajero: data.turno.cajero || null,
          desde: data.turno.fecha_apertura || null,
          propia: data.es_turno_propio !== undefined
            ? Boolean(data.es_turno_propio)
            : (Number(data.turno.usuario_apertura_id) === Number(usuario?.id) || String(data.turno.cajero || '').toLowerCase() === String(usuario?.username || '').toLowerCase()),
        });
      } else {
        setEstadoCaja({ abierta: false, cajero: null, desde: null, propia: false });
      }
    } catch {
      setEstadoCaja({ abierta: false, cajero: null, desde: null, propia: false });
    }
  }, [usuario?.id, usuario?.username]);

  // Si CUALQUIER pantalla recibe un 401 (token vencido/inválido), api.js
  // dispara este callback y volvemos directo al login.
  useEffect(() => {
    setOnSesionExpirada(() => setUsuario(null));
  }, []);

  // Refrescar el estado de caja al cargar y cada vez que se entra a la
  // sección Caja o Ventas (donde puede cambiar abrir/cerrar).
  useEffect(() => {
    if (usuario) cargarEstadoCaja();
  }, [usuario, vista, cargarEstadoCaja]);

  const cargarContextos=React.useCallback(async()=>{
    if(!usuario){setContextos([]);return;}
    try{const r=await api.get('/api/contextos');setContextos(r.contextos||[]);}catch{setContextos([]);}
  },[usuario]);

  useEffect(()=>{cargarContextos();},[cargarContextos]);

  const cambiarContexto=async(valor)=>{
    if(!valor)return;
    if(!navigator.onLine){alert('No se puede cambiar de empresa, sucursal o depósito sin conexión. Sincronizá primero las ventas pendientes.');return;}
    const [empresa_id,sucursal_id,deposito_id]=valor.split(':').map(Number);
    setCambiandoContexto(true);
    try{
      const data=await api.post('/api/contextos/seleccionar',{empresa_id,sucursal_id,deposito_id});
      setSesion(data.token,data.user);
      setUsuario(data.user);
      setVista('dashboard');
      window.location.reload();
    }finally{setCambiandoContexto(false);}
  };

  const opcionesContexto=contextos.flatMap(c=>(c.depositos||[]).map(d=>({
    value:`${c.empresa_id}:${c.sucursal_id}:${d.id}`,
    label:`${c.empresa_nombre} · ${c.sucursal_nombre} · ${d.nombre}`,
  })));
  const contextoActual=usuario?`${usuario.empresa_id}:${usuario.sucursal_id}:${usuario.deposito_id}`:'';
  const contextoActivo=contextos.find(c=>Number(c.empresa_id)===Number(usuario?.empresa_id)&&Number(c.sucursal_id)===Number(usuario?.sucursal_id));
  const depositoActivo=contextoActivo?.depositos?.find(d=>Number(d.id)===Number(usuario?.deposito_id));
  const sucursalActivaNombre=contextoActivo?.sucursal_nombre||usuario?.sucursal_nombre||branding.nombre_negocio||'Sucursal';
  const empresaActivaNombre=contextoActivo?.empresa_nombre||usuario?.empresa_nombre||branding.nombre_negocio||'Empresa';
  const depositoActivoNombre=depositoActivo?.nombre||usuario?.deposito_nombre||'Depósito';

  const cerrarSesion = () => {
    limpiarSesion();
    setUsuario(null);
  };

  if (!usuario) {
    return <Login onLogin={setUsuario} branding={branding} />;
  }

  // 2. AGREGAMOS EL TÍTULO PARA LA BARRA SUPERIOR
  const titulos = {
    dashboard: 'Dashboard', ventas: 'Terminal de Ventas', historial: 'Historial de Ventas',
    inventario: 'Stock e Inventario', kardex: 'Kardex de Stock', inventarioFisico: 'Inventario físico', clientes: 'Clientes y Fiados', proveedores: 'Proveedores',
    compras: 'Compras / Facturas de Proveedor', reposicion: 'Reposición inteligente y pedidos', precios: 'Costos y formación de precios',
    caja: 'Control de Caja', facturacion: 'Facturación electrónica', reportes: 'Reportes', promos: 'Módulo Promociones', config: 'Configuración', usuarios: 'Gestión del Personal', organizacion: 'Empresas, sucursales y depósitos', transferencias: 'Transferencias de stock', lotes: 'Lotes y vencimientos', mercadoPago: 'Mercado Pago automático'
  };

  const menuItems = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard' },
    { id: 'ventas', icon: '🛒', label: 'Terminal de Ventas' },
    { id: 'historial', icon: '🧾', label: 'Historial de Ventas' },
    { id: 'caja', icon: '💰', label: 'Control de Caja' },
    { id: 'inventario', icon: '📦', label: 'Stock e Inventario' },
    { id: 'kardex', icon: '📚', label: 'Kardex de Stock' },
    { id: 'clientes', icon: '👥', label: 'Clientes y Fiados' },
    { id: 'proveedores', icon: '🚚', label: 'Proveedores' }
  ];

  const rolActual=String(usuario.rol||'cajero').toLowerCase();
  const esAdmin=['admin','propietario'].includes(rolActual);
  const puede=(permiso)=>esAdmin||usuario.permisos?.[permiso]===true;

  const adminItems = [
    { id: 'organizacion', icon: '🏢', label: 'Empresas y depósitos', permiso: 'gestionar_organizacion' },
    { id: 'transferencias', icon: '🔄', label: 'Transferencias de stock', permiso: 'transferir_stock' },
    { id: 'lotes', icon: '📅', label: 'Lotes y vencimientos', permiso: 'gestionar_stock' },
    { id: 'inventarioFisico', icon: '📋', label: 'Inventario físico', permiso: 'gestionar_stock' },
    { id: 'compras', icon: '🛒', label: 'Compras (Facturas)', permiso: 'gestionar_compras' },
    { id: 'reposicion', icon: '📦', label: 'Reposición y pedidos', permiso: 'gestionar_compras' },
    { id: 'facturacion', icon: '🏛️', label: 'Facturación electrónica', permiso: 'gestionar_facturacion' },
    { id: 'precios', icon: '🏷️', label: 'Costos y precios', permiso: 'gestionar_precios' },
    { id: 'reportes', icon: '📈', label: 'Reportes y Libro IVA', permiso: 'ver_reportes' },
    { id: 'promos', icon: '🎁', label: 'Módulo Promociones', permiso: 'gestionar_promociones' },
    { id: 'usuarios', icon: '🧑‍💻', label: 'Gestión del Personal', permiso: 'gestionar_usuarios' },
    { id: 'mercadoPago', icon: '💙', label: 'Mercado Pago automático', permiso: 'gestionar_configuracion' },
    { id: 'config', icon: '⚙️', label: 'Configuración', permiso: 'gestionar_configuracion' }
  ];
  const adminItemsVisibles=adminItems.filter((item)=>puede(item.permiso));

  const fechaFormat = new Date().toLocaleDateString('es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div style={{ display: 'flex', height: '100vh', backgroundColor: '#f4f7fb', overflow: 'hidden' }}>
      
      {/* 1. SIDEBAR IZQUIERDA FIJA */}
      <div style={{ 
        width: '260px', backgroundColor: '#0b1121', color: 'white', display: 'flex', flexDirection: 'column',
        position: esMovil ? 'absolute' : 'relative', zIndex: 100, height: '100%',
        transform: esMovil && !menuOpen ? 'translateX(-100%)' : 'translateX(0)', transition: 'transform 0.3s ease'
      }}>
        
        <div style={{ padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', textAlign: branding.personalizado ? 'center' : 'left' }}>
          {branding.personalizado && branding.logo_url && (
            <img src={branding.logo_url} alt={sucursalActivaNombre} style={{ maxWidth: '190px', maxHeight: '66px', objectFit: 'contain', marginBottom: '8px' }} />
          )}
          <h1 style={{ fontSize: '20px', margin: 0, fontWeight: '800', color: 'white' }}>{sucursalActivaNombre}</h1>
          <h2 style={{ fontSize: '12px', margin: '4px 0 0 0', color: '#38bdf8', letterSpacing: '1px' }}>POS PRO</h2>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '15px 10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {menuItems.map(item => (
            <button key={item.id} onClick={() => { setVista(item.id); setMenuOpen(false); }} 
              style={{ ...btnSidebar, backgroundColor: vista === item.id ? '#2563eb' : 'transparent', color: vista === item.id ? 'white' : '#94a3b8' }}>
              <span style={{ fontSize:'18px' }}>{item.icon}</span> {item.label}
            </button>
          ))}

          {/* MUESTRA EL MENÚ PROTEGIDO SOLO SI ES ADMIN */}
          {adminItemsVisibles.length>0 && (
            <>
              <div style={{ marginTop:'15px', marginBottom:'5px', paddingLeft:'15px', fontSize:'11px', fontWeight:'bold', color:'#475569', textTransform:'uppercase' }}>Administración</div>
              {adminItemsVisibles.map(item => (
                <button key={item.id} onClick={() => { setVista(item.id); setMenuOpen(false); }} 
                  style={{ ...btnSidebar, backgroundColor: vista === item.id ? '#2563eb' : 'transparent', color: vista === item.id ? 'white' : '#94a3b8' }}>
                  <span style={{ fontSize:'18px' }}>{item.icon}</span> {item.label}
                </button>
              ))}
            </>
          )}
        </div>

        {/* Tarjeta de sucursal y cerrar sesión */}
        <div style={{ padding: '20px' }}>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px', marginBottom: '15px' }}>
            <p style={{margin:0,fontSize:'13px',fontWeight:'bold'}}>{sucursalActivaNombre}</p>
            <p style={{margin:'5px 0 0',fontSize:'12px',color:'#94a3b8'}}>{empresaActivaNombre}</p>
            <p style={{margin:'3px 0 0',fontSize:'11px',color:'#64748b'}}>{depositoActivoNombre}</p>
          </div>
          <button onClick={cerrarSesion} style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#cbd5e1', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
            Cerrar sesión
          </button>
        </div>
      </div>

      {/* P0.14: overlay oscuro detrás del menú móvil. Al tocarlo se cierra. */}
      {esMovil && menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 90 }}
        />
      )}

      {/* 2. ÁREA PRINCIPAL (Topbar + Contenido) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        
        {/* TOPBAR */}
        <div style={{ backgroundColor: 'white', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {esMovil && (
              <button onClick={() => setMenuOpen(!menuOpen)} style={{ background:'none', border:'none', fontSize:'24px', cursor:'pointer' }}>☰</button>
            )}
            <h2 style={{ margin: 0, fontSize: '20px', color: '#0f172a', fontWeight: '600' }}>{titulos[vista]}</h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {opcionesContexto.length>1&&<select value={contextoActual} onChange={e=>cambiarContexto(e.target.value)} disabled={cambiandoContexto||!online} title={online?'Cambiar empresa, sucursal o depósito':'No disponible sin conexión'} style={{maxWidth:esMovil?'155px':'310px',padding:'7px 9px',border:'1px solid #cbd5e1',borderRadius:'7px',background:'#fff',fontSize:'12px'}}>{opcionesContexto.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>}
            <OfflineStatus usuario={usuario} esMovil={esMovil} />
            <span style={{ fontSize: '13px', color: '#64748b', textTransform: 'capitalize', display: esMovil ? 'none' : 'block' }}>{fechaFormat}</span>
            {estadoCaja.abierta ? (
              <div title={estadoCaja.desde ? `Abierta desde ${new Date(estadoCaja.desde).toLocaleTimeString('es-AR')}` : ''}
                style={{ backgroundColor: estadoCaja.propia ? '#ecfdf5' : '#fff7ed', color: estadoCaja.propia ? '#059669' : '#c2410c', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', border: `1px solid ${estadoCaja.propia ? '#a7f3d0' : '#fdba74'}` }}>
                {estadoCaja.propia ? '🟢 Mi caja abierta' : '🟠 Caja de otro usuario'}{estadoCaja.cajero && !esMovil ? ` · ${estadoCaja.cajero}` : ''}
              </div>
            ) : (
              <div style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', border: '1px solid #fecaca' }}>
                🔴 Caja cerrada
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderLeft: '1px solid #e2e8f0', paddingLeft: '20px' }}>
              <div style={{ width: '36px', height: '36px', backgroundColor: '#3b82f6', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                {usuario.username.charAt(0).toUpperCase()}
              </div>
              <span style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>{usuario.username}</span>
            </div>
          </div>
        </div>

        {/* CONTENIDO DINÁMICO */}
        <div style={{ flex: 1, padding: '24px', overflowY: 'auto', position: 'relative' }}>
          
          {/* 🚀 EL TRUCO: La Terminal de Ventas queda siempre viva en el fondo. 
              Solo la ocultamos visualmente cuando vas a otra pestaña. */}
          <div style={{ display: vista === 'ventas' ? 'block' : 'none', height: '100%' }}>
            <ErrorBoundary titulo="La Terminal de Ventas tuvo un problema">
              <Ventas usuario={usuario} activa={vista === 'ventas'} />
            </ErrorBoundary>
          </div>
          <div style={{ display: vista === 'promos' ? 'block' : 'none', height: '100%' }}>
            <ErrorBoundary titulo="El módulo de Promociones tuvo un problema">
              <Promociones />
            </ErrorBoundary>
          </div>

          {/* 4. RENDERIZAMOS LA PANTALLA DE USUARIOS SI LA SELECCIONAN */}
          {vista === 'dashboard' && (
            <ErrorBoundary titulo="El Dashboard tuvo un problema">
              <Dashboard usuario={usuario} setVista={setVista} setFiltroInventario={setFiltroInventario} branding={branding} nombreSucursal={sucursalActivaNombre} nombreEmpresa={empresaActivaNombre} />
            </ErrorBoundary>
          )}
          {vista === 'historial' && (
            <ErrorBoundary titulo="El Historial de Ventas tuvo un problema">
              <HistorialVentas usuario={usuario} />
            </ErrorBoundary>
          )}
          {vista === 'inventario' && (
            <ErrorBoundary titulo="El Inventario tuvo un problema">
              <Inventario filtroExterno={filtroInventario} setFiltroExterno={setFiltroInventario} />
            </ErrorBoundary>
          )}
          {vista === 'kardex' && (
            <ErrorBoundary titulo="El Kardex tuvo un problema">
              <Kardex />
            </ErrorBoundary>
          )}
          {vista === 'inventarioFisico' && (
            <ErrorBoundary titulo="El Inventario físico tuvo un problema">
              <InventariosFisicos />
            </ErrorBoundary>
          )}
          {vista === 'caja' && (
            <ErrorBoundary titulo="El Control de Caja tuvo un problema">
              <Caja onEstadoCajaChange={cargarEstadoCaja} usuario={usuario} />
            </ErrorBoundary>
          )}
          {vista === 'precios' && (
            <ErrorBoundary titulo="Costos y precios tuvo un problema">
              <Precios />
            </ErrorBoundary>
          )}
          {vista === 'facturacion' && (
            <ErrorBoundary titulo="La Facturación tuvo un problema">
              <Facturacion />
            </ErrorBoundary>
          )}
          {vista === 'reportes' && (
            <ErrorBoundary titulo="Los Reportes tuvieron un problema">
              <Reportes branding={branding} />
            </ErrorBoundary>
          )}
          {vista === 'clientes' && (
            <ErrorBoundary titulo="Clientes y Fiados tuvo un problema">
              <Clientes usuario={usuario} />
            </ErrorBoundary>
          )}
          {vista === 'proveedores' && (
            <ErrorBoundary titulo="Proveedores tuvo un problema">
              <Proveedores />
            </ErrorBoundary>
          )}
          {vista === 'compras' && (
            <ErrorBoundary titulo="Compras tuvo un problema">
              <Compras />
            </ErrorBoundary>
          )}
          {vista === 'reposicion' && (
            <ErrorBoundary titulo="Reposición y pedidos tuvo un problema">
              <Reposicion />
            </ErrorBoundary>
          )}
          {vista === 'organizacion' && (
            <ErrorBoundary titulo="La estructura empresarial tuvo un problema">
              <Organizacion usuario={usuario} onContextosChange={cargarContextos}/>
            </ErrorBoundary>
          )}
          {vista === 'transferencias' && (
            <ErrorBoundary titulo="Las transferencias tuvieron un problema">
              <Transferencias usuario={usuario}/>
            </ErrorBoundary>
          )}
          {vista === 'lotes' && (
            <ErrorBoundary titulo="Lotes y vencimientos tuvo un problema">
              <Lotes />
            </ErrorBoundary>
          )}
          {vista === 'mercadoPago' && (
            <ErrorBoundary titulo="Mercado Pago tuvo un problema">
              <MercadoPago />
            </ErrorBoundary>
          )}
          {vista === 'config' && (
            <ErrorBoundary titulo="La Configuración tuvo un problema">
              <Configuracion branding={branding} onBrandingChange={actualizarBranding} />
            </ErrorBoundary>
          )}
          {vista === 'usuarios' && (
            <ErrorBoundary titulo="La Gestión del Personal tuvo un problema">
              <Usuarios />
            </ErrorBoundary>
          )}
        </div>
      </div>

    </div>
  );
}

// Estilo para botones del sidebar
const btnSidebar = {
  width: '100%', padding: '12px 15px', borderRadius: '8px', border: 'none', 
  display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', 
  fontWeight: '600', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s'
};